import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LangContext";
import { Clock, MapPin, ExternalLink } from "lucide-react";

const MAPS_QUERY = "Masia+La+Torre+del+Gall,+Sant+Cugat+Sesgarrigues,+Barcelona";

const Details = () => {
  const { t } = useLang();
  const mapsLink = `https://www.google.com/maps/search/?api=1&query=${MAPS_QUERY}`;
  const embedSrc = `https://www.google.com/maps?q=${MAPS_QUERY}&output=embed`;

  return (
    <section
      id="details"
      data-testid="details-section"
      className="py-28 md:py-40 px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="font-serif text-4xl md:text-5xl text-wine text-center mb-16"
        >
          {t.hero.dateLong}
        </motion.h2>

        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-stretch">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="bg-white rounded-2xl p-10 md:p-12 shadow-[0_8px_30px_rgb(107,45,49,0.06)] border border-olive/10 flex flex-col justify-center"
          >
            <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-4">
              {t.details.ceremonyTitle}
            </p>
            <div className="flex items-center gap-3 mb-6">
              <Clock className="w-5 h-5 text-wine" strokeWidth={1.5} />
              <span className="font-serif text-2xl md:text-3xl text-wine">{t.details.time}</span>
            </div>
            <div className="flex items-start gap-3 mb-8">
              <MapPin className="w-5 h-5 text-wine mt-1" strokeWidth={1.5} />
              <div>
                <p className="font-serif text-xl md:text-2xl text-charcoal">{t.details.venue}</p>
                <p className="font-editorial italic text-base text-olive mt-1">{t.details.address}</p>
                <p className="font-editorial italic text-base text-olive">{t.details.city}</p>
                <p className="font-editorial italic text-base text-olive">{t.details.region}</p>
              </div>
            </div>
            <a
              data-testid="open-maps-link"
              href={mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 self-start bg-wine text-cream px-7 py-3.5 rounded-full text-[11px] tracking-[0.25em] uppercase transition-all duration-500 hover:bg-wine-deep hover:scale-[1.02] hover:shadow-lg"
            >
              {t.details.openMaps}
              <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.8} />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="rounded-2xl overflow-hidden border border-olive/10 shadow-[0_8px_30px_rgb(107,45,49,0.06)] min-h-[360px] md:min-h-full"
          >
            <iframe
              data-testid="venue-map"
              title="Masia La Torre del Gall map"
              src={embedSrc}
              className="w-full h-full min-h-[360px] map-elegant"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Details;
